import { Box, CircularProgress, Paper, TablePagination, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePricing } from '../../contexts/PricingContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getUser, updateUserPreferences } from '../../services/userService';
import type { Tender } from '../../types/tender';
import ArchiveTenderDialog from '../Dialogs/ArchiveTenderDialog';
import PublicTenderCard from './Table/PublicTenderCard';
import PublicTenderDetailsDialog from './Table/PublicTenderDetailsDialog';
import PublicTendersFilters from './Table/PublicTendersFilters';
import { isTenderClosed, parseSubmissionDeadlineInTable } from './Table/tableUtils';

interface PublicTendersTableProps {
  tenders: Tender[];
  onAddTender: (tender: Tender) => Promise<void>;
  onRemoveTender: (tender: Tender, reason: string) => void;
  isLoading: boolean;
  pursuedTendersMap?: Record<string, { ownerName: string; teamName: string; ownerId: string }>;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface UserKeywords {
  [keyword: string]: number;
}

interface ReadStatus {
  [tenderId: string]: boolean;
}

const PublicTendersTable: React.FC<PublicTendersTableProps> = ({
  tenders,
  onAddTender,
  onRemoveTender,
  isLoading,
  pursuedTendersMap,
}) => {
  const { idToken } = useAuth();
  const { account } = useAccount();
  const { pricingSettings } = usePricing();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  const [filteredTenders, setFilteredTenders] = useState<Tender[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('ALL');
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'llmProcessingTimestamp',
    direction: 'desc',
  });
  const [hasUserManuallySorted, setHasUserManuallySorted] = useState(false);
  const [userKeywords, setUserKeywords] = useState<UserKeywords>({});
  const [tenderRelevanceScores, setTenderRelevanceScores] = useState(new Map<string, number>());
  const [hideClosedTenders, setHideClosedTenders] = useState<boolean>(false);

  const [removeConfirmationDialogOpen, setRemoveConfirmationDialogOpen] = useState(false);
  const [tenderToAction, setTenderToAction] = useState<Tender | null>(null);

  const [isAddingTenderId, setIsAddingTenderId] = useState<string | null>(null);

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedTenderForDetails, setSelectedTenderForDetails] = useState<Tender | null>(null);
  const [readStatus, setReadStatus] = useState<ReadStatus>({});

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    const fetchUserPreferences = async () => {
      if (!account?.uid || !account?.clientId) {
        return;
      }
      try {
        const userData = await getUser(account.uid);
        if (userData) {
          // biome-ignore lint/suspicious/noExplicitAny: Expected
          const uData = userData as any;
          if (uData.tenderKeywords && Object.keys(uData.tenderKeywords).length > 0) {
            setUserKeywords(uData.tenderKeywords);
            if (!hasUserManuallySorted) {
              setSortConfig({ key: 'relevance', direction: 'desc' });
            }
          } else {
            setUserKeywords({});
            if (sortConfig.key === 'relevance') {
              setSortConfig({ key: 'llmProcessingTimestamp', direction: 'desc' });
            }
          }
          if (uData.defaultPublicTenderSearch) {
            setSearchTerm(uData.defaultPublicTenderSearch);
          }
          if (uData.defaultPublicTenderSource) {
            setSourceFilter(uData.defaultPublicTenderSource);
          }
          if (uData.defaultPublicTenderState) {
            setStateFilter(uData.defaultPublicTenderState);
          }
        } else {
          setUserKeywords({});
          if (sortConfig.key === 'relevance') {
            setSortConfig({ key: 'llmProcessingTimestamp', direction: 'desc' });
          }
        }
      } catch (_error) {
        setUserKeywords({});
        if (sortConfig.key === 'relevance') {
          setSortConfig({ key: 'llmProcessingTimestamp', direction: 'desc' });
        }
      }
    };
    fetchUserPreferences();
  }, [account?.uid, account?.clientId, hasUserManuallySorted, sortConfig.key]);

  const handleSaveDefault = async () => {
    if (!account?.uid) {
      return;
    }
    try {
      await updateUserPreferences(account.uid, {
        defaultPublicTenderSearch: searchTerm,
        defaultPublicTenderSource: sourceFilter,
        defaultPublicTenderState: stateFilter,
      });
      showSnackbar('Search preferences saved as default.', 'success');
    } catch (_error) {
      showSnackbar('Failed to save search preferences.', 'error');
    }
  };

  const calculateRelevanceScoreOptimized = useCallback(
    (tender: Tender): number => {
      if (!tender.keywords || Object.keys(userKeywords).length === 0) {
        return 0;
      }
      let tenderKeywords = tender.keywords;
      if (!Array.isArray(tenderKeywords)) {
        tenderKeywords = String(tenderKeywords)
          .split(',')
          .map((kw) => kw.trim())
          .filter(Boolean);
      }
      const normalizedTenderKeywords = tenderKeywords.map((kw) => kw.trim().toLowerCase());
      let score = 0;
      const matchedKeywords = new Set<string>();

      for (const tenderKw of normalizedTenderKeywords) {
        let bestMatchScore = 0;
        let bestMatch: string | null = null;
        for (const userKeyword of Object.keys(userKeywords)) {
          if (matchedKeywords.has(userKeyword)) {
            continue;
          }
          const userKwNormalized = userKeyword.toLowerCase();
          const userKwCount = userKeywords[userKeyword];
          if (tenderKw === userKwNormalized) {
            if (userKwCount && userKwCount > bestMatchScore) {
              bestMatchScore = userKwCount;
              bestMatch = userKeyword;
            }
          } else if (tenderKw.includes(userKwNormalized)) {
            const partialScore = (userKwCount || 0) * 0.8;
            if (partialScore > bestMatchScore) {
              bestMatchScore = partialScore;
              bestMatch = userKeyword;
            }
          } else if (userKwNormalized.includes(tenderKw)) {
            const partialScore = (userKwCount || 0) * 0.6;
            if (partialScore > bestMatchScore) {
              bestMatchScore = partialScore;
              bestMatch = userKeyword;
            }
          }
        }
        if (bestMatch && bestMatchScore > 0) {
          score += bestMatchScore;
          matchedKeywords.add(bestMatch);
        }
      }
      return score;
    },
    [userKeywords]
  );

  useEffect(() => {
    if (Object.keys(userKeywords).length === 0 || !tenders || tenders.length === 0) {
      setTenderRelevanceScores(new Map());
      return;
    }
    const scoreMap = new Map<string, number>();
    for (const tender of tenders) {
      const score = calculateRelevanceScoreOptimized(tender);
      const tenderId = tender.tenderId || tender.opportunityID || tender.id;
      if (tenderId) {
        scoreMap.set(tenderId, score);
      }
    }
    setTenderRelevanceScores(scoreMap);
  }, [userKeywords, tenders, calculateRelevanceScoreOptimized]);

  const getCachedRelevanceScore = useCallback(
    (tender: Tender): number => {
      const tenderId = tender.tenderId || tender.opportunityID || tender.id;
      if (tenderId) {
        return tenderRelevanceScores.get(tenderId) || 0;
      }
      return 0;
    },
    [tenderRelevanceScores]
  );

  useEffect(() => {
    let newFilteredTenders = [...tenders];

    if (sourceFilter !== 'ALL') {
      newFilteredTenders = newFilteredTenders.filter((tender) => {
        const tid = tender.tenderId || tender.opportunityID || tender.id || '';
        if (sourceFilter === 'VP') {
          return tid.startsWith('VP');
        }
        if (sourceFilter === 'ATM') {
          return tid.startsWith('ATM');
        }
        if (sourceFilter === 'BNSW') {
          return tid.startsWith('BNSW_');
        }
        if (sourceFilter === 'TVIC') {
          return tid.startsWith('TVIC_');
        }
        if (sourceFilter === 'TWA') {
          return tid.startsWith('TWA_');
        }
        if (sourceFilter === 'SAT') {
          return tid.startsWith('SAT_');
        }
        return true;
      });
    }

    if (stateFilter.length > 0) {
      newFilteredTenders = newFilteredTenders.filter((tender) => {
        const tState = (tender.state || tender.location || '').toUpperCase();
        return stateFilter.some((s) => tState.includes(s)) || tState.includes('NATIONAL');
      });
    }

    if (hideClosedTenders) {
      newFilteredTenders = newFilteredTenders.filter((tender) => {
        const deadlineDateObj = parseSubmissionDeadlineInTable(tender.submissionDeadline || '');
        return !isTenderClosed(deadlineDateObj);
      });
    }

    if (searchTerm.trim()) {
      const orSegments = searchTerm
        .split(/\s+OR\s+/i)
        .map((s) => s.trim())
        .filter(Boolean);
      newFilteredTenders = newFilteredTenders.filter((tender) => {
        const searchableFields = [
          tender.title,
          tender.tenderIssuer,
          tender.tenderId,
          tender.opportunityID,
          tender.id,
          tender.scope,
          tender.description,
          tender.state,
          tender.location,
          ...(Array.isArray(tender.keywords)
            ? tender.keywords
            : String(tender.keywords || '').split(',')),
        ].map((val) => String(val || '').toLowerCase());
        const matchesTerm = (term: string) =>
          searchableFields.some((field) => field.includes(term.toLowerCase()));
        return orSegments.some((orSegment) => {
          const andSegments = orSegment
            .split(/\s+AND\s+/i)
            .map((s) => s.trim())
            .filter(Boolean);
          return andSegments.every((andSegment) => {
            if (andSegment.toUpperCase().startsWith('NOT ')) {
              const negativeTerm = andSegment.substring(4).trim();
              if (!negativeTerm) {
                return true;
              }
              return !matchesTerm(negativeTerm);
            }
            return matchesTerm(andSegment);
          });
        });
      });
    }

    if (sortConfig.key) {
      if (
        sortConfig.key === 'relevance' &&
        Object.keys(userKeywords).length > 0 &&
        tenderRelevanceScores.size === 0
      ) {
        return;
      }
      newFilteredTenders.sort((a, b) => {
        let aValue: unknown, bValue: unknown;
        if (sortConfig.key === 'relevance') {
          aValue = getCachedRelevanceScore(a);
          bValue = getCachedRelevanceScore(b);
          if ((aValue as number) < (bValue as number)) {
            return sortConfig.direction === 'desc' ? 1 : -1;
          }
          if ((aValue as number) > (bValue as number)) {
            return sortConfig.direction === 'desc' ? -1 : 1;
          }
          return 0;
        } else if (sortConfig.key === 'submissionDeadline') {
          const dateA = parseSubmissionDeadlineInTable(a.submissionDeadline || '');
          const dateB = parseSubmissionDeadlineInTable(b.submissionDeadline || '');
          aValue = dateA ? dateA.getTime() : sortConfig.direction === 'asc' ? Infinity : -Infinity;
          bValue = dateB ? dateB.getTime() : sortConfig.direction === 'asc' ? Infinity : -Infinity;
        } else if (sortConfig.key === 'llmProcessingTimestamp') {
          const dateA = a.llmProcessingTimestamp
            ? a.llmProcessingTimestamp instanceof Date
              ? a.llmProcessingTimestamp
              : new Date(a.llmProcessingTimestamp)
            : null;
          const dateB = b.llmProcessingTimestamp
            ? b.llmProcessingTimestamp instanceof Date
              ? b.llmProcessingTimestamp
              : new Date(b.llmProcessingTimestamp)
            : null;
          aValue = dateA ? dateA.getTime() : sortConfig.direction === 'asc' ? Infinity : -Infinity;
          bValue = dateB ? dateB.getTime() : sortConfig.direction === 'asc' ? Infinity : -Infinity;
        } else if (sortConfig.key === 'title') {
          aValue = (a.title || 'TitleMissing').toLowerCase();
          bValue = (b.title || 'TitleMissing').toLowerCase();
        } else if (sortConfig.key === 'client') {
          aValue = (a.tenderIssuer || 'IssuerMissing').toLowerCase();
          bValue = (b.tenderIssuer || 'IssuerMissing').toLowerCase();
        } else {
          aValue = a[sortConfig.key as keyof Tender];
          bValue = b[sortConfig.key as keyof Tender];
          if (aValue instanceof Date) {
            aValue = aValue.getTime();
          }
          if (bValue instanceof Date) {
            bValue = bValue.getTime();
          }
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          } else if (Array.isArray(aValue) || typeof aValue === 'object') {
            return 0;
          }
        }
        if (sortConfig.key !== 'relevance') {
          if ((aValue as number) < (bValue as number)) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if ((aValue as number) > (bValue as number)) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        }
        return 0;
      });
    }
    setFilteredTenders(newFilteredTenders);
  }, [
    searchTerm,
    sourceFilter,
    stateFilter,
    hideClosedTenders,
    tenders,
    sortConfig,
    userKeywords,
    tenderRelevanceScores,
    getCachedRelevanceScore,
  ]);

  const handleAddTender = async (tender: Tender) => {
    if (!idToken) {
      showSnackbar('Authentication session expired. Please refresh.', 'error');
      return;
    }
    setIsAddingTenderId(tender.id || null);
    try {
      await onAddTender(tender);
      showSnackbar(
        `Tender "${tender.title}" added to your dashboard successfully. Generating the capability report now!`,
        'success'
      );
    } catch (_error) {
      // Error handled by parent
    } finally {
      setIsAddingTenderId(null);
    }
  };

  const handleOpenDetailsDialog = (tender: Tender) => {
    setSelectedTenderForDetails(tender);
    setDetailsDialogOpen(true);
    if (tender.id) {
      setReadStatus((prev) => ({ ...prev, [tender.id as string]: true }));
    }
  };

  return (
    <Box>
      <PublicTendersFilters
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        sortConfig={sortConfig}
        onSortChange={(e) => {
          const [key, direction] = (e.target.value as string).split('-');
          setSortConfig({ key: key as string, direction: direction as 'asc' | 'desc' });
          setHasUserManuallySorted(true);
        }}
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
        stateFilter={stateFilter}
        onStateFilterChange={setStateFilter}
        hideClosedTenders={hideClosedTenders}
        onHideClosedTendersChange={setHideClosedTenders}
        onSaveSearch={handleSaveDefault}
        hasKeywords={Object.keys(userKeywords).length > 0}
      />

      {isLoading && filteredTenders.length === 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography ml={2}>Loading public tenders...</Typography>
        </Box>
      )}
      {!isLoading && tenders.length === 0 && (
        <Paper
          sx={{
            textAlign: 'center',
            mt: 3,
            p: 3,
            fontStyle: 'italic',
            background: theme.palette.greyLight,
            color: theme.palette.greyContrast,
            borderRadius: 1,
          }}
        >
          <Typography>Public Tenders is currently empty.</Typography>
        </Paper>
      )}
      {!isLoading && tenders.length > 0 && filteredTenders.length === 0 && searchTerm && (
        <Paper
          sx={{
            textAlign: 'center',
            mt: 3,
            p: 3,
            fontStyle: 'italic',
            background: theme.palette.greyLight,
            color: theme.palette.greyContrast,
            borderRadius: 1,
          }}
        >
          <Typography>No public tenders match your search criteria.</Typography>
        </Paper>
      )}

      <Box sx={{ mt: 2 }}>
        {(rowsPerPage > 0
          ? filteredTenders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : filteredTenders
        ).map((tender) => (
          <PublicTenderCard
            key={tender.id}
            tender={tender}
            isRead={!!(tender.id && readStatus[tender.id])}
            isPursued={!!(tender.id && pursuedTendersMap?.[tender.id])}
            isAdding={isAddingTenderId === tender.id}
            isLoading={isLoading}
            {...(tender.id && pursuedTendersMap?.[tender.id]
              ? { pursuedInfo: pursuedTendersMap[tender.id] }
              : {})}
            relevanceScore={getCachedRelevanceScore(tender)}
            onOpenDetails={() => handleOpenDetailsDialog(tender)}
            onAddTender={() => handleAddTender(tender)}
            onArchive={() => {
              setTenderToAction(tender);
              setRemoveConfirmationDialogOpen(true);
            }}
            pricingSettings={pricingSettings}
          />
        ))}
      </Box>

      <TablePagination
        component="div"
        count={filteredTenders.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Tenders per page:"
        slotProps={{ select: { variant: 'outlined', size: 'small' } }}
        sx={{
          borderTop: `1px solid ${theme.palette.greyContrast}`,
          mt: 2,
          color: theme.palette.text.primary,
          fontSize: '0.9rem',
          '.MuiTablePagination-toolbar': {
            display: { xs: 'grid', sm: 'flex' },
            gridTemplateColumns: { xs: 'auto auto' },
            gridTemplateRows: { xs: 'auto auto' },
            gridTemplateAreas: { xs: `'label select' 'caption actions'` },
            alignItems: 'center',
            justifyContent: 'flex-end',
            columnGap: { xs: '16px', sm: '8px' },
            rowGap: { xs: '8px', sm: '0px' },
          },
          '.MuiTablePagination-selectLabel': { gridArea: 'label', justifySelf: 'start' },
          '.MuiTablePagination-select': { gridArea: 'select', justifySelf: 'start' },
          '.MuiTablePagination-caption': { gridArea: 'caption', justifySelf: 'start' },
          '.MuiTablePagination-actions': { gridArea: 'actions', justifySelf: 'end' },
          '.MuiTablePagination-spacer': { display: 'none' },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: theme.palette.text.primary,
            fontSize: '0.9rem',
          },
          '& .MuiOutlinedInput-root': { borderRadius: '8px' },
          '& .MuiSelect-icon': { color: theme.palette.text.primary },
          '& .MuiButtonBase-root.Mui-disabled': { color: theme.palette.action.disabled },
        }}
      />

      <ArchiveTenderDialog
        open={removeConfirmationDialogOpen}
        onClose={() => {
          setRemoveConfirmationDialogOpen(false);
          setTenderToAction(null);
        }}
        onConfirm={(params) => {
          if (tenderToAction && params.reason) {
            onRemoveTender(tenderToAction, params.reason);
            setRemoveConfirmationDialogOpen(false);
            setTenderToAction(null);
          } else {
            showSnackbar('Please provide a reason for hiding the tender.', 'warning');
          }
        }}
        tenderTitle={tenderToAction?.title}
        tenderId={tenderToAction?.id}
      />

      <PublicTenderDetailsDialog
        open={detailsDialogOpen}
        tender={selectedTenderForDetails}
        onClose={() => setDetailsDialogOpen(false)}
        onAddTender={handleAddTender}
        onArchive={(tender) => {
          setTenderToAction(tender);
          setRemoveConfirmationDialogOpen(true);
        }}
        isAdding={isAddingTenderId === selectedTenderForDetails?.id}
        isLoading={isLoading}
        isPursued={
          !!(selectedTenderForDetails?.id && pursuedTendersMap?.[selectedTenderForDetails.id])
        }
        pricingSettings={pricingSettings}
      />
    </Box>
  );
};

export default PublicTendersTable;
